import { FEATURE } from "@/features";
import { Button } from "@components/ui/button";
import { Feature } from "@components/Feature";
import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemActions,
} from "@components/ui/item";
import { Trans, useLingui } from "@lingui/react/macro";
import { MailIcon, MessageSquareIcon } from "lucide-react";
import { SEARCH_KIND, FILTER } from "@/constants";
import { ROUTES } from "@/routes_new";
import { formatDateForApi, stringifyQuery } from "@/utils";
import { getMentionViewDateRanges } from "@/views/MentionsView/MentionLayout/ranges";
import { useMemo } from "react";
import { generatePath, useParams } from "react-router-dom";
import { Search } from "@/types/Search";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import { useMutation } from "@tanstack/react-query";
import {
  requestAsyncExport,
  ASYNC_EXPORT_FORMAT,
  AsyncExportFormat,
} from "@/api/endpoints/async-export";
import { toast } from "sonner";
import { Spinner } from "@components/ui/spinner";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";

export default function Mentions({
  filters,
  search,
  onClose,
}: {
  search: Nullable<Search>;
  filters: SearchFilterV4;
  onClose?: () => void;
}) {
  const { t } = useLingui();
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Merge URL filters with default date range for non-SAVED searches.
  // Used by both sync download URLs and the async export request.
  const resolvedFilters = useMemo(() => {
    let defaults = {};

    if (search?.kind !== SEARCH_KIND.SAVED) {
      const defaultRange = getMentionViewDateRanges().find(
        (range) => range.default
      );
      if (defaultRange) {
        defaults = {
          [FILTER.START_DATE]: defaultRange.startDate
            ? formatDateForApi(defaultRange.startDate)
            : null,
          [FILTER.END_DATE]: defaultRange.endDate
            ? formatDateForApi(defaultRange.endDate)
            : null,
        };
      }
    }

    return { ...defaults, ...filters };
  }, [filters, search]);

  const stringifiedFilters = useMemo(
    () => stringifyQuery(resolvedFilters),
    [resolvedFilters]
  );

  const excelUrl =
    search &&
    `${generatePath(ROUTES.SEARCH_EXPORT_EXCEL, {
      search_uuid: search.uuid,
    })}?${stringifiedFilters}`;

  const csvUrl =
    search &&
    `${generatePath(ROUTES.SEARCH_EXPORT_CSV, {
      search_uuid: search.uuid,
    })}?${stringifiedFilters}`;

  // Async export mutation
  const { mutate: startAsyncExport, isPending } = useMutation({
    mutationFn: (format: AsyncExportFormat) => {
      if (!orgUuid) throw new Error("Missing orgUuid");

      const trackEvent =
        format === ASYNC_EXPORT_FORMAT.EXCEL
          ? EVENT.EXPORT_MENTIONS_TO_EXCEL_ASYNC
          : EVENT.EXPORT_MENTIONS_TO_CSV_ASYNC;
      postTrackingData(trackEvent);

      return requestAsyncExport({
        format,
        org_uuid: orgUuid,
        search_uuid: search?.uuid,
        filters: resolvedFilters,
      });
    },
    onSuccess: (res) => {
      if (!res.ok) throw new Error();

      toast.success(
        t`Exporten har startats. Du får ett mejl med en nedladdningslänk när den är klar.`
      );
      onClose?.();
    },
    onError: () => {
      toast.error(t`Kunde inte starta exporten. Försök igen.`);
    },
  });

  const { isVisible: showSpinner } = useDelayedLoading(isPending);

  return (
    <Feature required={FEATURE.HAS_EXCEL_EXPORT_FEATURE}>
      {(excelUrl || csvUrl) && (
        <Item variant="outline" size="sm">
          <ItemMedia variant="icon">
            <MessageSquareIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>
              <Trans>Omnämnanden</Trans>
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            {excelUrl && (
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid="excel-export-link"
                onClick={() => {
                  postTrackingData(EVENT.EXPORT_MENTIONS_TO_EXCEL);
                }}
              >
                <a href={excelUrl}>
                  <Trans>Exportera till Excel</Trans>
                </a>
              </Button>
            )}

            {csvUrl && (
              <Button
                asChild
                size="sm"
                onClick={() => {
                  postTrackingData(EVENT.EXPORT_MENTIONS_TO_CSV);
                }}
              >
                <a href={csvUrl}>
                  <Trans>Exportera till CSV</Trans>
                </a>
              </Button>
            )}
          </ItemActions>
        </Item>
      )}

      <AccessControl level={ACCESS_LEVELS.STAFF}>
        {orgUuid && (
          <Item variant="outline" size="sm">
            <ItemMedia variant="icon">
              <MailIcon />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>
                <Trans>Omnämnanden (bakgrund)</Trans>
              </ItemTitle>
            </ItemContent>
            <ItemActions>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isPending}
                data-testid="excel-export-async-link"
                onClick={() => startAsyncExport(ASYNC_EXPORT_FORMAT.EXCEL)}
              >
                <Trans>Excel via mejl</Trans>
                {showSpinner && <Spinner />}
              </Button>

              <Button
                type="button"
                size="sm"
                disabled={isPending}
                data-testid="csv-export-async-link"
                onClick={() => startAsyncExport(ASYNC_EXPORT_FORMAT.CSV)}
              >
                <Trans>CSV via mejl</Trans>
                {showSpinner && <Spinner />}
              </Button>
            </ItemActions>
          </Item>
        )}
      </AccessControl>
    </Feature>
  );
}
